import { useState, useCallback } from 'react';
import './index.css';
import {
  AreaChart, Area,
  LineChart, Line,
  XAxis, YAxis,
  CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from 'recharts';

// ─── Parsers ──────────────────────────────────────────────────────────────────

function parseValue(html, label) {
  const re = new RegExp(label + '[^<]*<\\/span><\\/td><td>([^<]+)<', 'i');
  const m = html.match(re);
  return m ? m[1].trim() : null;
}

function parseMwh(str) {
  if (!str) return null;
  return parseInt(str.replace(/[^0-9]/g, ''), 10);
}

function parseCapacityHistory(html) {
  const tableRe = /<table[^>]*>([\s\S]*?)<\/table>/gi;
  const rows = [];
  let tm;
  while ((tm = tableRe.exec(html)) !== null) {
    const tbl = tm[1];
    if (
      /FULL CHARGE CAPACITY/i.test(tbl) &&
      /DESIGN CAPACITY/i.test(tbl) &&
      /PERIOD/i.test(tbl)
    ) {
      const rowRe = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
      let rm;
      while ((rm = rowRe.exec(tbl)) !== null) {
        const cells = [];
        const cellRe = /<td[^>]*>([\s\S]*?)<\/td>/gi;
        let cm;
        while ((cm = cellRe.exec(rm[1])) !== null) {
          cells.push(cm[1].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim());
        }
        if (cells.length >= 3) {
          const dm = cells[0].match(/(\d{4}-\d{2}-\d{2})/);
          const full = parseMwh(cells[1]);
          const design = parseMwh(cells[2]);
          if (dm && full && design) {
            rows.push({
              date: dm[1],
              full,
              design,
              health: Math.round((full / design) * 100),
            });
          }
        }
      }
    }
  }
  const seen = {};
  rows.forEach(r => { seen[r.date] = r; });
  return Object.values(seen).sort((a, b) => a.date.localeCompare(b.date));
}

function predictReplacement(history, currentHealth) {
  if (!history || history.length < 6) return null;
  const recent = history.slice(-20);
  const n = recent.length;
  const xs = recent.map((_, i) => i);
  const ys = recent.map(r => r.health);
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  const num = xs.reduce((s, x, i) => s + (x - mx) * (ys[i] - my), 0);
  const den = xs.reduce((s, x) => s + (x - mx) ** 2, 0);
  const slope = num / den;
  if (slope >= 0) return null;
  const daySpan =
    (new Date(recent[n - 1].date) - new Date(recent[0].date)) / 86400000;
  const ratePerDay = (recent[0].health - recent[n - 1].health) / daySpan;
  if (ratePerDay <= 0) return null;
  const daysTo = target => {
    const d = (currentHealth - target) / ratePerDay;
    return d > 0 ? new Date(Date.now() + d * 86400000) : null;
  };
  return { to80: daysTo(80), to60: daysTo(60), ratePerDay };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return 'Already passed';
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

function healthColor(h) {
  if (h >= 80) return '#16a34a';
  if (h >= 60) return '#d97706';
  return '#dc2626';
}

function healthBg(h) {
  if (h >= 80) return '#f0fdf4';
  if (h >= 60) return '#fffbeb';
  return '#fef2f2';
}

function healthBorder(h) {
  if (h >= 80) return '#bbf7d0';
  if (h >= 60) return '#fde68a';
  return '#fecaca';
}

function healthLabel(h) {
  if (h >= 80) return 'Good';
  if (h >= 60) return 'Fair';
  return 'Poor';
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: '10px 14px',
      fontSize: 12,
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <div style={{ color: '#9ca3af', marginBottom: 4, fontWeight: 500 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, fontWeight: 600 }}>
          {p.name}:{' '}
          {p.name === 'Health %'
            ? `${p.value}%`
            : `${p.value != null ? p.value.toLocaleString() : '—'} mWh`}
        </div>
      ))}
    </div>
  );
}

// ─── Landing / Upload ─────────────────────────────────────────────────────────

function LandingPage({ onFile, dragging, onDragOver, onDragLeave, onDrop }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
    }}>
      {/* Logo */}
      <div style={{
        width: 68, height: 68, background: '#fff', borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 2px 16px rgba(0,0,0,0.09)', marginBottom: 26,
      }}>
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none"
          stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="16" height="10" rx="2" />
          <path d="M22 11v2" />
        </svg>
      </div>

      <h1 style={{
        fontSize: 'clamp(2.2rem, 5vw, 3rem)',
        fontWeight: 800,
        color: '#1a1a2e',
        letterSpacing: '-0.03em',
        marginBottom: 14,
        textAlign: 'center',
      }}>
        BatteryInsight
      </h1>

      <p style={{
        fontSize: 16,
        color: '#6b7280',
        textAlign: 'center',
        maxWidth: 400,
        lineHeight: 1.7,
        marginBottom: 44,
      }}>
        Upload your Windows battery report to get a health analysis,
        predictive lifespan, and replacement recommendations.
      </p>

      {/* Upload card */}
      <div
        className={`upload-zone${dragging ? ' dragging' : ''}`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => document.getElementById('file-input').click()}
        style={{ width: '100%', maxWidth: 500, padding: '2.8rem 2rem', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' }}
      >
        <div style={{
          width: 54, height: 54, background: '#f3f4f6', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 18px',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 16 12 12 8 16" />
            <line x1="12" y1="12" x2="12" y2="21" />
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
          </svg>
        </div>

        <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
          Drag &amp; drop your report
        </div>

        <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 26, lineHeight: 1.6 }}>
          Generate it by running{' '}
          <code style={{
            background: '#f3f4f6', borderRadius: 5,
            padding: '2px 8px', fontSize: 12, color: '#374151',
          }}>
            powercfg /batteryreport
          </code>
          {' '}in Windows Command Prompt.
        </div>

        <button
          onClick={e => { e.stopPropagation(); document.getElementById('file-input').click(); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 9,
            padding: '10px 22px', fontSize: 14, fontWeight: 600,
            color: '#374151', cursor: 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
            <polyline points="13 2 13 9 20 9" />
          </svg>
          Browse Files
        </button>

        <input
          id="file-input"
          type="file"
          accept=".html"
          onChange={e => onFile(e.target.files[0])}
        />
      </div>
    </div>
  );
}

// ─── Report View ──────────────────────────────────────────────────────────────

function ReportView({ report, onReset }) {
  const [tab, setTab] = useState('overview');
  const h = report.health;

  return (
    <div className="fade-in" style={{ maxWidth: 740, margin: '0 auto', padding: '3rem 1.5rem' }}>

      {/* Tabs */}
      <div style={{
        display: 'inline-flex', background: '#f3f4f6',
        borderRadius: 9, padding: 3, marginBottom: '1.25rem', gap: 2,
      }}>
        {[['overview', 'Overview'], ['graph', 'History'], ['prediction', 'Prediction']].map(([id, label]) => (
          <button
            key={id}
            className={`tab-btn${tab === id ? ' active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Health card */}
          <div className="card" style={{ padding: '1.5rem', display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Gauge */}
            <div style={{ position: 'relative', width: 110, height: 110, flexShrink: 0 }}>
              <svg width="110" height="110" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="55" cy="55" r="44" fill="none" stroke="#f3f4f6" strokeWidth="9" />
                <circle
                  cx="55" cy="55" r="44" fill="none"
                  stroke={healthColor(h)} strokeWidth="9" strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 44}`}
                  strokeDashoffset={`${2 * Math.PI * 44 * (1 - h / 100)}`}
                  style={{ transition: 'stroke-dashoffset 1s ease' }}
                />
              </svg>
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: healthColor(h), lineHeight: 1 }}>{h}%</div>
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2, fontWeight: 500 }}>HEALTH</div>
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: healthBg(h), border: `1px solid ${healthBorder(h)}`,
                borderRadius: 6, padding: '4px 10px', marginBottom: 12,
              }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: healthColor(h) }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: healthColor(h) }}>{healthLabel(h)}</span>
              </div>

              <div>
                {[
                  ['Manufacturer', report.manufacturer],
                  ['Chemistry', report.chemistry],
                  ['Serial Number', report.serialNumber],
                  ['Cycle Count', report.cycles],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="meta-row">
                    <span style={{ color: '#9ca3af', fontWeight: 500 }}>{k}</span>
                    <span style={{ color: '#374151', fontWeight: 500 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
            {[
              { label: 'Design Capacity', value: report.designRaw, sub: 'Original' },
              { label: 'Full Charge', value: report.fullRaw || '—', sub: 'Current' },
              {
                label: 'Capacity Lost',
                value: report.design && report.full
                  ? `${(report.design - report.full).toLocaleString()} mWh`
                  : '—',
                sub: report.design && report.full
                  ? `${((report.design - report.full) / 1000).toFixed(2)} Wh`
                  : 'Degraded',
              },
            ].map(s => (
              <div key={s.label} className="stat-card">
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', letterSpacing: '0.04em', marginBottom: 8, textTransform: 'uppercase' }}>{s.label}</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#111827', marginBottom: 2 }}>{s.value}</div>
                <div style={{ fontSize: 11, color: '#d1d5db' }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Wear bar */}
          <div className="card" style={{ padding: '1.25rem 1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Battery Wear</span>
              <span style={{ fontSize: 13, color: '#9ca3af' }}>{100 - h}% degraded</span>
            </div>
            <div style={{ background: '#f3f4f6', borderRadius: 99, height: 8, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${100 - h}%`,
                background: healthColor(h), borderRadius: 99,
                transition: 'width 1s ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: '#d1d5db' }}>
              <span>New</span>
              <span>Consider replacing at 40% worn</span>
              <span>Worn out</span>
            </div>
          </div>
        </div>
      )}

      {/* ── History ── */}
      {tab === 'graph' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          <div className="card" style={{ padding: '1.5rem' }}>
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 3 }}>Charge Capacity Over Time</div>
              <div style={{ fontSize: 13, color: '#9ca3af' }}>Full charge capacity vs. original design capacity</div>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={report.chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradDesign" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.1} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradFull" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#16a34a" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#16a34a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#f3f4f6" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  tickLine={false} axisLine={false}
                  interval={Math.floor(report.chartData.length / 6)}
                />
                <YAxis
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  tickLine={false} axisLine={false}
                  tickFormatter={v => `${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="design" name="Design Cap"
                  stroke="#6366f1" strokeWidth={1.5} fill="url(#gradDesign)"
                  dot={false} strokeDasharray="5 3" />
                <Area type="monotone" dataKey="full" name="Full Charge"
                  stroke="#16a34a" strokeWidth={2} fill="url(#gradFull)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', gap: 20, marginTop: 12, fontSize: 12 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6b7280' }}>
                <span style={{ width: 20, height: 2, background: '#16a34a', display: 'inline-block', borderRadius: 2 }} />
                Full Charge Capacity
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6b7280' }}>
                <span style={{ width: 20, height: 2, background: '#6366f1', display: 'inline-block', borderRadius: 2 }} />
                Design Capacity
              </span>
            </div>
          </div>

          <div className="card" style={{ padding: '1.5rem' }}>
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 3 }}>Health % Over Time</div>
              <div style={{ fontSize: 13, color: '#9ca3af' }}>Battery health relative to original design capacity</div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={report.chartData} margin={{ top: 5, right: 40, left: -15, bottom: 0 }}>
                <CartesianGrid stroke="#f3f4f6" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  tickLine={false} axisLine={false}
                  interval={Math.floor(report.chartData.length / 6)}
                />
                <YAxis
                  domain={[0, 105]}
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  tickLine={false} axisLine={false}
                  tickFormatter={v => `${v}%`}
                />
                <Tooltip content={<ChartTooltip />} />
                <ReferenceLine y={80} stroke="#d97706" strokeDasharray="4 3" strokeWidth={1.5}
                  label={{ value: '80%', fill: '#d97706', fontSize: 11 }} />
                <ReferenceLine y={60} stroke="#dc2626" strokeDasharray="4 3" strokeWidth={1.5}
                  label={{ value: '60%', fill: '#dc2626', fontSize: 11 }} />
                <Line type="monotone" dataKey="Health %" stroke="#111827" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 12 }}>
              <span style={{ color: '#d97706' }}>— 80% Fair threshold</span>
              <span style={{ color: '#dc2626' }}>— 60% Replace threshold</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Prediction ── */}
      {tab === 'prediction' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {report.prediction ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {[
                  {
                    label: 'Fair Threshold (80%)',
                    date: report.prediction.to80,
                    desc: 'Battery performance starts declining',
                    color: '#d97706', bg: '#fffbeb', border: '#fde68a',
                  },
                  {
                    label: 'Replace Threshold (60%)',
                    date: report.prediction.to60,
                    desc: 'Recommended replacement date',
                    color: '#dc2626', bg: '#fef2f2', border: '#fecaca',
                  },
                ].map(s => (
                  <div key={s.label} style={{
                    background: s.bg, border: `1px solid ${s.border}`,
                    borderRadius: 10, padding: '1.25rem',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: s.color, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
                      {s.label}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                      {fmtDate(s.date)}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{s.desc}</div>
                  </div>
                ))}
              </div>

              <div className="card" style={{ padding: '1.25rem 1.5rem' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                  Degradation Rate
                </div>
                <div style={{ fontSize: 26, fontWeight: 700, color: '#111827' }}>
                  {(report.prediction.ratePerDay * 30).toFixed(2)}%
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af', marginLeft: 6 }}>per month</span>
                </div>
                <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>Based on recent degradation trend</div>
              </div>

              <div className="card" style={{ padding: '1.25rem 1.5rem' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12 }}>
                  Tips to Extend Battery Life
                </div>
                {[
                  'Keep charge between 20–80% for daily use',
                  'Avoid leaving plugged in at 100% for extended periods',
                  'Keep the laptop cool — avoid soft surfaces that block vents',
                  'Calibrate monthly: fully drain, then fully charge',
                  'Reduce screen brightness and disable unused background apps',
                ].map((tip, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 10, padding: '9px 0',
                    borderBottom: i < 4 ? '1px solid #f3f4f6' : 'none',
                    fontSize: 13, color: '#374151', alignItems: 'flex-start',
                  }}>
                    <span style={{ color: '#9ca3af', flexShrink: 0, marginTop: 1 }}>→</span>
                    <span>{tip}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="card" style={{ padding: '2.5rem', textAlign: 'center' }}>
              <div style={{ fontSize: 13, color: '#9ca3af' }}>
                Not enough history to generate a prediction. At least 6 months of data is required.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{
        marginTop: '1.5rem', paddingTop: '1rem',
        borderTop: '1px solid #f3f4f6',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 12, color: '#d1d5db' }}>
          {report.history ? report.history.length : 0} data points analyzed
        </span>
        <button
          onClick={onReset}
          style={{
            background: 'none', border: '1px solid #e5e7eb', borderRadius: 7,
            color: '#6b7280', padding: '7px 16px', fontSize: 13,
            cursor: 'pointer', fontWeight: 500,
          }}
        >
          Upload New Report
        </button>
      </div>
    </div>
  );
}

// ─── Error Banner ─────────────────────────────────────────────────────────────

function ErrorBanner({ message, onRetry }) {
  return (
    <div style={{
      background: '#fef2f2', border: '1px solid #fecaca',
      borderRadius: 10, padding: '16px 18px', marginTop: 12,
      maxWidth: 500, width: '100%',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#dc2626', marginBottom: 4 }}>
        Unable to read file
      </div>
      <div style={{ fontSize: 13, color: '#991b1b' }}>{message}</div>
      <button
        onClick={onRetry}
        style={{
          marginTop: 12, background: '#dc2626', color: '#fff',
          border: 'none', borderRadius: 6, padding: '7px 16px',
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}
      >
        Try Again
      </button>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);

  const processFile = useCallback((file) => {
    if (!file || !file.name.endsWith('.html')) {
      setError('Please upload an HTML file generated by: powercfg /batteryreport');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const html = e.target.result;
      const designRaw    = parseValue(html, 'DESIGN CAPACITY');
      const fullRaw      = parseValue(html, 'FULL CHARGE CAPACITY');
      const cycleRaw     = parseValue(html, 'CYCLE COUNT');
      const manufacturer = parseValue(html, 'MANUFACTURER');
      const chemistry    = parseValue(html, 'CHEMISTRY');
      const serialNumber = parseValue(html, 'SERIAL NUMBER');

      if (!designRaw) {
        setError(
          'Could not find "Design Capacity". Make sure you upload the HTML file ' +
          'generated by running powercfg /batteryreport in an Administrator Command Prompt.'
        );
        setReport(null);
        return;
      }

      const design  = parseMwh(designRaw);
      const full    = parseMwh(fullRaw);
      const health  = design && full ? Math.round((full / design) * 100) : null;
      const cycles  = cycleRaw ? parseInt(cycleRaw.replace(/[^0-9]/g, ''), 10) : null;
      const history = parseCapacityHistory(html);
      const prediction = health ? predictReplacement(history, health) : null;

      const stride = Math.max(1, Math.floor(history.length / 60));
      const chartData = history
        .filter((_, i) => i % stride === 0 || i === history.length - 1)
        .map(r => ({
          date: r.date.slice(0, 7),
          full: r.full,
          design: r.design,
          'Health %': r.health,
        }));

      setReport({
        design, full, health, cycles,
        manufacturer, chemistry, serialNumber,
        designRaw, fullRaw,
        history, chartData, prediction,
      });
      setError(null);
    };
    reader.readAsText(file);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files[0]);
  }, [processFile]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const onDragLeave = useCallback(() => {
    setDragging(false);
  }, []);

  const handleReset = useCallback(() => {
    setReport(null);
    setError(null);
  }, []);

  if (report) {
    return <ReportView report={report} onReset={handleReset} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <LandingPage
        onFile={processFile}
        dragging={dragging}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      />
      {error && (
        <div style={{ position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)', zIndex: 100, width: '90%', maxWidth: 500 }}>
          <ErrorBanner message={error} onRetry={() => { setError(null); document.getElementById('file-input').click(); }} />
        </div>
      )}
    </div>
  );
}
